import type { TFunction } from "i18next";

import { ScheduleCadence, TimeOfDayString } from "shared/api/schedules/types";
import { TIME_ONLY } from "shared/constants";
import {
  formatDate,
  formatDayOfMonth,
  formatList,
  formatWeekday,
} from "shared/locale/format";

const formatScheduleTimeOfDay = (timeOfDay: TimeOfDayString): string =>
  formatDate(`2000-01-03T${timeOfDay}:00.000Z`, TIME_ONLY, false, false, "UTC");

const formatScheduleCadence = (
  t: TFunction,
  scheduleCadence: ScheduleCadence,
  timezone: string
): string => {
  switch (scheduleCadence.cadence) {
    case "hourly":
      return t("common:schedule_summary.hourly");
    case "daily":
      return t("common:schedule_summary.daily", {
        timeOfDay: formatScheduleTimeOfDay(scheduleCadence.timeOfDay),
        tz: timezone,
      });
    case "weekly":
      return t("common:schedule_summary.weekly", {
        days: formatList(scheduleCadence.days.map(formatWeekday)),
        timeOfDay: formatScheduleTimeOfDay(scheduleCadence.timeOfDay),
        tz: timezone,
      });
    case "monthlyFirstDay":
      return t("common:schedule_summary.monthly_first_day", {
        day: formatWeekday(scheduleCadence.day),
        timeOfDay: formatScheduleTimeOfDay(scheduleCadence.timeOfDay),
        tz: timezone,
      });
    case "monthlyDate":
      return t("common:schedule_summary.monthly_date", {
        dayOfMonth: formatDayOfMonth(scheduleCadence.dayOfMonth),
        timeOfDay: formatScheduleTimeOfDay(scheduleCadence.timeOfDay),
        tz: timezone,
      });
  }
};

// One line for a schedule's cadences, or null when it has none: what "none" reads as is the caller's to say.
export const getScheduleSummary = (
  t: TFunction,
  cadences: ScheduleCadence[],
  timezone: string
): string | null =>
  cadences.length
    ? cadences
        .map((cadence) => formatScheduleCadence(t, cadence, timezone))
        .join("; ")
    : null;
